// Fitting a face to what it draws (TOM-606, a local change: see VENDORED.md).
// A dial or bar gauge is drawn on the whole 400 × 300 face, with empty bands
// and margins around it; fitted, its viewBox is its drawn parts, so a host
// that frames the face itself (its own card and title) gets a gauge as wide
// as its card. The element's `fit` (web.js) and a page's faces (page.js,
// TOM-587) fit the same way.
import { getDefinition } from "./registry.js";

/**
 * Faces that keep their drawing with no reading (Mike, 2026-10-14, TOM-665):
 * the scale, rose or plot stays, the value is a muted "—" with no fill,
 * pointer or needle, and the status corner says MISSING or DISCONNECTED.
 * Every other face draws "Source unavailable" in its place (render.js). A
 * face that keeps its drawing is measured for its fit with no reading too,
 * so it has the size it has with one. A number keeps its face too (TOM-956):
 * a quiet dash with its unit, the size its reading will be.
 */
export const FACE_WITHOUT_READING = Object.freeze(
  new Set([
    "arc-gauge",
    "history",
    "needle-gauge",
    "number",
    "vacuum",
    "wind-rose",
  ]),
);

// The faces that fit: dials and bar gauges, whose drawn parts keep their
// place whatever the reading.
const fittedFamilies = new Set(["angular", "gauge"]);

/**
 * Faces fitted to their value (TOM-912): a number panel's value, its unit and
 * any second reading fill about 80% of the panel, with a tenth of the drawn
 * size left clear on each side, rather than a quarter of the whole 400 × 300
 * face. The readout, a number's own design, is fitted to its digits instead
 * (TOM-956, `READOUT_FIT`).
 */
export const FACE_FITTED_TO_VALUE = Object.freeze(new Set(["number"]));

/** The share of the drawn size left clear on each side of a face fitted to its value. */
export const VALUE_FIT_MARGIN = 0.1;

/**
 * The readout's share of its panel (TOM-956, cloud ADR 0460): its reading
 * grows until it is 88% of the panel's width or its digits are 70% of the
 * panel's height, whichever comes first.
 */
export const READOUT_FIT = Object.freeze({ width: 0.88, height: 0.7 });

/**
 * A digit's height in ems of its font size: SF Pro Rounded's figures stand
 * at its cap height, 0.70 em, as most faces' do.
 */
export const DIGIT_HEIGHT = 0.7;

/** True for a rendered face that fits: a dial, a bar gauge or a number, with no picture. */
export function faceFits(rendered) {
  return (
    (fittedFamilies.has(getDefinition(rendered.definitionId).family) ||
      FACE_FITTED_TO_VALUE.has(rendered.definitionId)) &&
    !rendered.data?.media
  );
}

/**
 * The box a face is fitted to, grown by this paint. It is measured from a
 * settled face showing a reading and never shrunk, so a moving needle or a
 * longer number never resizes it; a face with no reading, or one still
 * moving, keeps the box it has. `box` is the box so far, or null before the
 * first measurement. The caller forgets it when the face's options change,
 * since options can move the face's parts.
 */
export function measureFace(svg, rendered, phase, box) {
  const toValue = FACE_FITTED_TO_VALUE.has(rendered.definitionId);
  if (
    ["missing", "disconnected"].includes(rendered.quality) &&
    !FACE_WITHOUT_READING.has(rendered.definitionId)
  )
    return box;
  if (phase === "animating") return box;
  let drawn = null;
  try {
    const face = svg.querySelector(".control-face");
    drawn = toValue ? visibleBox(svg, face) : (face?.getBBox() ?? null);
  } catch {
    // Not laid out (a hidden page): measure on a later paint.
  }
  if (!drawn || drawn.width <= 0 || drawn.height <= 0) return box;
  // A readout's margins make its reading READOUT_FIT of the box each way
  // (TOM-956); any other number keeps a tenth of it clear (TOM-912).
  const margin = (size, share) => (size * (1 / share - 1)) / 2;
  const padX = drawn.digits
      ? margin(drawn.width, READOUT_FIT.width)
      : toValue
        ? Math.max(6, drawn.width * VALUE_FIT_MARGIN)
        : 6,
    padY = drawn.digits
      ? margin(drawn.height, READOUT_FIT.height)
      : toValue
        ? Math.max(6, drawn.height * VALUE_FIT_MARGIN)
        : 6,
    [x0, y0, x1, y1] = box ?? [400, 300, 0, 0];
  return [
    Math.max(0, Math.min(x0, Math.floor(drawn.x - padX))),
    Math.max(0, Math.min(y0, Math.floor(drawn.y - padY))),
    Math.min(400, Math.max(x1, Math.ceil(drawn.x + drawn.width + padX))),
    Math.min(300, Math.max(y1, Math.ceil(drawn.y + drawn.height + padY))),
  ];
}

/**
 * A value as JSON with every object's keys in order, for telling whether a
 * face's options or bindings changed (TOM-587). The iPhone builds each
 * update's instance afresh from Swift dictionaries, whose keys come out in a
 * new order each time: compared as plain JSON, half its updates looked like
 * new options, and each one forgot the face's box, so the gauge was drawn on
 * the whole face until its motion ended.
 */
export function stableJSON(value) {
  return JSON.stringify(value, (_, inner) =>
    inner && typeof inner === "object" && !Array.isArray(inner)
      ? Object.fromEntries(
          Object.entries(inner).sort(([a], [b]) => (a < b ? -1 : 1)),
        )
      : inner,
  );
}

/** Parts of a face that hold no drawing of their own. */
const UNDRAWN = new Set([
  "clippath",
  "defs",
  "desc",
  "lineargradient",
  "mask",
  "radialgradient",
  "style",
  "title",
]);

/**
 * The box a clip-path (`url(#id)`) lets through, from its rect or circle,
 * or null when the clip is something else.
 */
function clipBox(svg, element) {
  const ref = /^url\(#([^)]+)\)$/.exec(
    element.getAttribute?.("clip-path") ?? "",
  );
  if (!ref) return null;
  const shape = svg.querySelector(`[id="${ref[1]}"]`)?.firstElementChild;
  const n = (name) => Number(shape?.getAttribute(name));
  if (shape?.tagName.toLowerCase() === "rect")
    return [n("x"), n("y"), n("x") + n("width"), n("y") + n("height")];
  if (shape?.tagName.toLowerCase() === "circle")
    return [
      n("cx") - n("r"),
      n("cy") - n("r"),
      n("cx") + n("r"),
      n("cy") + n("r"),
    ];
  return null;
}

/**
 * A readout's text as its digits stand (TOM-956): from its baseline up by
 * `DIGIT_HEIGHT` of its size, as wide as the text runs. getBBox runs from the
 * font's ascent to its descent, about half as tall again as the digits, so a
 * panel fitted to it drew them at about half its height. Null for any other
 * part.
 */
function digitBox(part, b) {
  if (part.getAttribute?.("data-fit") !== "digits") return null;
  const size = Number(part.getAttribute("font-size")),
    baseline = Number(part.getAttribute("y"));
  if (!(size > 0) || !Number.isFinite(baseline)) return null;
  return {
    x: b.x,
    y: baseline - size * DIGIT_HEIGHT,
    width: b.width,
    height: size * DIGIT_HEIGHT,
  };
}

/**
 * What a face shows, as a box (TOM-912): the union of its parts, each clipped
 * part cut to its clip. getBBox counts what a clip hides, and a number's
 * reels hold the digit above and below the one shown, so its box was three
 * digits tall. A readout's text counts its digits (`digitBox`), and the box
 * says so (`digits`). Null when nothing is drawn.
 */
function visibleBox(svg, face) {
  if (!face) return null;
  let box = null,
    digits = false;
  for (const part of face.children ?? []) {
    if (UNDRAWN.has(part.tagName.toLowerCase())) continue;
    let b = part.getBBox();
    if (!b || (b.width <= 0 && b.height <= 0)) continue;
    const standing = digitBox(part, b);
    if (standing) {
      b = standing;
      digits = true;
    }
    let edges = [b.x, b.y, b.x + b.width, b.y + b.height];
    const clip = clipBox(svg, part);
    if (clip && clip.every(Number.isFinite))
      edges = [
        Math.max(edges[0], clip[0]),
        Math.max(edges[1], clip[1]),
        Math.min(edges[2], clip[2]),
        Math.min(edges[3], clip[3]),
      ];
    if (edges[2] < edges[0] || edges[3] < edges[1]) continue;
    box = box
      ? [
          Math.min(box[0], edges[0]),
          Math.min(box[1], edges[1]),
          Math.max(box[2], edges[2]),
          Math.max(box[3], edges[3]),
        ]
      : edges;
  }
  return (
    box && {
      x: box[0],
      y: box[1],
      width: box[2] - box[0],
      height: box[3] - box[1],
      digits,
    }
  );
}

/** Draw the face to `box`. A paint writes the whole face again, so every paint fits it again. */
export function applyFit(svg, box) {
  const [x0, y0, x1, y1] = box;
  svg.setAttribute("viewBox", `${x0} ${y0} ${x1 - x0} ${y1 - y0}`);
}
